import { parseAsBoolean, parseAsInteger, parseAsString, parseAsStringLiteral, useQueryState } from 'nuqs'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import type { FindingsSortValue } from '../components/findings-sort-dropdown.tsx'
import { useGetScanFindingsWithFilters } from '../components/tables/filter-state-store.tsx'
import {
  ALL_FINDING_QUICK_VIEW_IDS,
  createFindingQuickViewSelectItem,
  DEFAULT_FINDING_QUICK_VIEW_ID,
  DEFAULT_SCA_FINDING_QUICK_VIEW_ID,
  getFindingQuickViewConfig,
  SAST_FINDING_QUICK_VIEW_IDS,
  SCA_FINDING_QUICK_VIEW_IDS,
  type FindingQuickViewId,
} from '../components/tables/finding-quick-view-config'
import {
  useGetAnalyses,
  useGetRepositoryFromHalLink,
  useGetScan,
  useGetScanFindings,
} from '../utils/api-client/api-hooks.ts'
import type { FindingAggregateResponseStatistics } from '../utils/api-client/api-schemas.ts'
import { isScaTool } from '../utils/detector-utils.ts'
import { calculateFindingsWithResultsHasAnyInProgressAnalysis } from '../utils/finding-utils.ts'
import { clearFirstBatchData, getFirstBatchData, isFirstBatchComplete } from '../utils/first-batch-tracking.ts'

export function useSearchQueryParam() {
  return useQueryState('search', parseAsString.withDefault(''))
}

export function useSortQueryParam() {
  return useQueryState(
    'sort',
    parseAsStringLiteral([
      'pixee-intelligence',
      'pixee-severity',
      'tool-severity',
      'effective-cvss',
    ] satisfies FindingsSortValue[]).withDefault('pixee-intelligence')
  )
}

export function useSortDirectionQueryParam() {
  return useQueryState('ascending', parseAsBoolean.withDefault(false))
}

export function useGetScanAndRepository({ scanId }: { scanId: string }) {
  const { data: scan, isLoading: isScanLoading, isError: isScanError } = useGetScan({ id: scanId })

  const href = scan?._links?.repository?.href
  const {
    data: repository,
    isLoading: isRepositoryLoading,
    isError: isRepositoryError,
  } = useGetRepositoryFromHalLink({
    path: href,
  })

  return {
    scan,
    isScanLoading,
    isScanError,
    repository,
    isRepositoryLoading,
    isRepositoryError,
  }
}

export type ScanLifecycleState =
  | { type: 'initial-unanalyzed-scan' }
  | {
      type: 'first-analysis-batch-in-progress'
      statistics: FindingAggregateResponseStatistics
      firstBatchTotal: number
    }
  | { type: 'findings-with-analysis-complete'; statistics: FindingAggregateResponseStatistics }
  | { type: 'findings-with-analysis-complete-and-in-progress'; statistics: FindingAggregateResponseStatistics }

export type UseGetScanLifecycleStateReturn = {
  scanLifecycleState: ScanLifecycleState | undefined
  isScanLifecycleLoading: boolean
  isScanLifecycleError: boolean
  totalFindings: number | undefined
  skipFirstBatch: () => void
}

/** Resolve the denominator for the first-batch progress bar.
 *  When the batch was triggered via "analyze all" (triggeredAll), the exact count isn't known
 *  at trigger time — use statistics.total from the API instead. */
function resolveFirstBatchTotal(
  firstBatchData: ReturnType<typeof getFirstBatchData>,
  statistics: FindingAggregateResponseStatistics
): number {
  if (!firstBatchData) return statistics.total
  if (firstBatchData.triggeredAll) return statistics.total
  return firstBatchData.totalTriggered
}

/**
 * Unified hook for scan analysis state.
 * Computes whether a scan has any analyses, whether any are in progress,
 * and the appropriate header state for display.
 *
 * Uses local storage to track the first analysis batch for UX purposes.
 * This is a temporary client-side workaround until the backend supports batch tracking.
 *
 * When the first batch is in progress, polling is enabled to automatically detect completion.
 */
export function useGetScanLifecycleState({ scanId }: { scanId: string }): UseGetScanLifecycleStateReturn {
  // clearFirstBatchData writes to module-level storage, not React state, so it won't trigger a
  // re-render on its own. The counter forces an immediate re-evaluation instead of waiting up
  // to 2 s for the next polling tick to pick up the cleared state.
  const [, setForceRerender] = useState(0)
  const isTrackingFirstBatch = getFirstBatchData(scanId) !== null
  const [pollForInProgress, setPollForInProgress] = useState(false)
  const skipFirstBatch = useCallback(() => {
    clearFirstBatchData(scanId)
    setForceRerender(c => c + 1)
  }, [scanId])
  const { data, isLoading, isError } = useGetScanFindings({
    scanId,
    pagination: { pageIndex: 0, pageSize: 1 },
    refetchInterval: isTrackingFirstBatch || pollForInProgress ? 2000 : false,
  })

  if (!data || isLoading || isError) {
    return {
      scanLifecycleState: undefined,
      isScanLifecycleLoading: isLoading,
      isScanLifecycleError: isError,
      totalFindings: undefined,
      skipFirstBatch,
    }
  }

  const statistics: FindingAggregateResponseStatistics = {
    total: data.total,
    completed_analysis: data.completed_analysis,
    in_progress_analysis: data.in_progress_analysis,
    triage: data.triage,
    fix: data.fix,
    sca: data.sca,
    composite: data.composite,
  }

  const hasCompletedAnalyses = statistics.completed_analysis > 0
  const hasInProgressAnalysis =
    statistics.in_progress_analysis > 0 || calculateFindingsWithResultsHasAnyInProgressAnalysis(statistics)
  const hasTerminalAnalysis =
    statistics.triage.status.failed > 0 ||
    statistics.triage.status.no_recommendations > 0 ||
    statistics.fix.status.failed > 0 ||
    statistics.fix.status.no_recommendations > 0 ||
    statistics.sca.status.failed > 0 ||
    statistics.sca.status.blocked > 0 ||
    statistics.sca.status.excluded > 0
  const firstBatchComplete = isFirstBatchComplete({
    scanId,
    completedAnalysis: statistics.completed_analysis,
    totalFindings: statistics.total,
  })

  if (hasInProgressAnalysis !== pollForInProgress) {
    setPollForInProgress(hasInProgressAnalysis)
  }

  const scanLifecycleState: ScanLifecycleState = match({
    hasInProgressAnalysis,
    hasCompletedAnalyses,
    hasZeroFindings: statistics.total === 0,
    firstBatchComplete,
    isTrackingFirstBatch,
    hasTerminalAnalysis,
  })
    .with({ hasZeroFindings: true }, () => {
      clearFirstBatchData(scanId)
      return { type: 'findings-with-analysis-complete' as const, statistics }
    })
    .with({ hasInProgressAnalysis: true, firstBatchComplete: false }, () => {
      const firstBatchData = getFirstBatchData(scanId)
      return {
        type: 'first-analysis-batch-in-progress' as const,
        statistics,
        firstBatchTotal: resolveFirstBatchTotal(firstBatchData, statistics),
      }
    })
    .with({ hasInProgressAnalysis: true }, () => {
      clearFirstBatchData(scanId)
      return {
        type: 'findings-with-analysis-complete-and-in-progress' as const,
        statistics,
      }
    })
    .with({ hasCompletedAnalyses: true }, () => {
      clearFirstBatchData(scanId)
      return { type: 'findings-with-analysis-complete' as const, statistics }
    })
    .with({ isTrackingFirstBatch: true, firstBatchComplete: false }, () => {
      const firstBatchData = getFirstBatchData(scanId)
      return {
        type: 'first-analysis-batch-in-progress' as const,
        statistics,
        firstBatchTotal: resolveFirstBatchTotal(firstBatchData, statistics),
      }
    })
    .with({ hasTerminalAnalysis: true }, () => {
      clearFirstBatchData(scanId)
      return { type: 'findings-with-analysis-complete' as const, statistics }
    })
    .otherwise(() => ({ type: 'initial-unanalyzed-scan' as const }))

  return {
    scanLifecycleState,
    isScanLifecycleLoading: isLoading,
    isScanLifecycleError: isError,
    totalFindings: data.total,
    skipFirstBatch,
  }
}

/**
 * True while the scan has analyses actively running — covers both the initial batch and
 * subsequent batches over an already-analyzed scan. Drives the in-progress `refetchInterval`
 * shared by the main findings table and the quick-view count queries.
 */
export function isScanAnalysisInProgress(scanLifecycleState: ScanLifecycleState | undefined): boolean {
  return (
    scanLifecycleState?.type === 'first-analysis-batch-in-progress' ||
    scanLifecycleState?.type === 'findings-with-analysis-complete-and-in-progress'
  )
}

export type UseGetScanCancellationReturn = {
  /** `undefined` when the query was never asked (`enabled: false`), so it is not read as "none". */
  hasCancellation: boolean | undefined
  latestCancellationTimestamp: string | undefined
}

/**
 * Returns whether any analysis on the scan has been cancelled, and the timestamp of the most
 * recent cancellation transition. Drives the "Analysis was cancelled" empty state and the
 * scan-info tooltip cancellation line.
 *
 * `enabled: false` defers the request until a caller needs it, for surfaces that render one of
 * these per row. It also holds back the stale-marker cleanup below, so it is only for callers that
 * do not depend on that. `hasCancellation` is `undefined` until the query has answered, so a
 * deferred call cannot be mistaken for "nothing cancelled".
 */
export function useGetScanCancellation({
  scanId,
  enabled,
}: {
  scanId: string
  enabled?: boolean
}): UseGetScanCancellationReturn {
  const { data, isPending } = useGetAnalyses({
    page: { number: 0, size: 20 },
    filters: { scanIds: [scanId], states: ['cancelled'] },
    enabled,
  })

  const analyses = data?._embedded.items ?? []
  const latestCancellationTimestamp = analyses.reduce<string | undefined>((latest, analysis) => {
    const timestamp = analysis.current_state.timestamp
    if (!latest || timestamp > latest) {
      return timestamp
    }
    return latest
  }, undefined)

  const hasCancellation = isPending ? undefined : analyses.length > 0

  // Safety net for the case where the mutation's onSuccess callback never ran (e.g. the
  // pixee-platform `_links` serialization bug caused the frontend to throw during Zod parse):
  // if we discover cancelled analyses on refresh, clear the stale first-batch marker so the
  // lifecycle state can resolve out of `first-analysis-batch-in-progress`.
  useEffect(() => {
    if (hasCancellation) {
      clearFirstBatchData(scanId)
    }
  }, [hasCancellation, scanId])

  return {
    hasCancellation,
    latestCancellationTimestamp,
  }
}

export function useFindingQuickViewCounts({
  scanId,
}: {
  scanId: string
}): Record<FindingQuickViewId, number | undefined> {
  // Poll the per-view counts while analysis is running so they track the main findings table,
  // which already refetches live. Without this the counts hold their pre-trigger totals until a
  // manual page refresh, because nothing invalidates these queries at batch *completion*.
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const refetchInterval = isScanAnalysisInProgress(scanLifecycleState) ? 1000 : false

  const analyzedFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('analyzed').defaultFilterState,
    refetchInterval,
  })

  const validatedFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('validated').defaultFilterState,
    refetchInterval,
  })

  const readyToFixFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('ready-to-fix').defaultFilterState,
    refetchInterval,
  })

  const dismissableFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('dismissable').defaultFilterState,
    refetchInterval,
  })

  const unanalyzedFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('unanalyzed').defaultFilterState,
    refetchInterval,
  })

  const scaAnalyzedFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('sca-analyzed').defaultFilterState,
    refetchInterval,
  })

  const scaExploitableFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('sca-exploitable').defaultFilterState,
    refetchInterval,
  })

  const scaNotExploitableFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('sca-not-exploitable').defaultFilterState,
    refetchInterval,
  })

  const scaUnanalyzedFindings = useGetScanFindingsWithFilters({
    scanId,
    filterState: getFindingQuickViewConfig('sca-unanalyzed').defaultFilterState,
    refetchInterval,
  })

  return useMemo(
    () => ({
      'all-findings': undefined,
      analyzed: analyzedFindings.data?.total,
      validated: validatedFindings.data?.total,
      'ready-to-fix': readyToFixFindings.data?.total,
      dismissable: dismissableFindings.data?.total,
      unanalyzed: unanalyzedFindings.data?.total,
      'sca-analyzed': scaAnalyzedFindings.data?.total,
      'sca-exploitable': scaExploitableFindings.data?.total,
      'sca-not-exploitable': scaNotExploitableFindings.data?.total,
      'sca-unanalyzed': scaUnanalyzedFindings.data?.total,
    }),
    [
      analyzedFindings.data?.total,
      validatedFindings.data?.total,
      readyToFixFindings.data?.total,
      dismissableFindings.data?.total,
      unanalyzedFindings.data?.total,
      scaAnalyzedFindings.data?.total,
      scaExploitableFindings.data?.total,
      scaNotExploitableFindings.data?.total,
      scaUnanalyzedFindings.data?.total,
    ]
  )
}

export function useFindingQuickViewState({ scanId }: { scanId: string }) {
  const { scan } = useGetScanAndRepository({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const isSca = scan ? isScaTool(scan.detector) : false
  const quickViewIds = isSca ? SCA_FINDING_QUICK_VIEW_IDS : SAST_FINDING_QUICK_VIEW_IDS
  const defaultQuickViewId = isSca ? DEFAULT_SCA_FINDING_QUICK_VIEW_ID : DEFAULT_FINDING_QUICK_VIEW_ID

  const [selectedQuickView, setSelectedQuickViewRaw] = useQueryState(
    'quick-view',
    parseAsStringLiteral(ALL_FINDING_QUICK_VIEW_IDS)
  )

  const [, setPageNumber] = useQueryState('page-number', parseAsInteger.withDefault(0).withOptions({ history: 'push' }))

  const setSelectedQuickView = useCallback(
    (value: FindingQuickViewId) => {
      setSelectedQuickViewRaw(value)
      setPageNumber(0)
    },
    [setSelectedQuickViewRaw, setPageNumber]
  )

  const quickViewCounts = useFindingQuickViewCounts({ scanId })

  const isAnalysisComplete =
    scanLifecycleState?.type === 'findings-with-analysis-complete' ||
    scanLifecycleState?.type === 'findings-with-analysis-complete-and-in-progress'
  const analyzedCount = isSca ? quickViewCounts['sca-analyzed'] : quickViewCounts.analyzed
  const effectiveDefaultQuickViewId =
    isAnalysisComplete && analyzedCount === 0
      ? isSca
        ? ('sca-unanalyzed' as const)
        : ('unanalyzed' as const)
      : defaultQuickViewId

  const isValidForCurrentScanType = quickViewIds.includes(selectedQuickView as FindingQuickViewId)
  const resolvedQuickView =
    selectedQuickView && isValidForCurrentScanType ? selectedQuickView : effectiveDefaultQuickViewId

  const quickViews = useMemo(
    () => quickViewIds.map(id => createFindingQuickViewSelectItem(id, quickViewCounts[id])),
    [quickViewIds, quickViewCounts]
  )

  return useMemo(
    () => ({
      selectedQuickView: resolvedQuickView,
      setSelectedQuickView,
      quickViews,
    }),
    [resolvedQuickView, setSelectedQuickView, quickViews]
  )
}

export function useSwitchToAnalyzedOnFirstBatchComplete({ scanId }: { scanId: string }) {
  const { setSelectedQuickView } = useFindingQuickViewState({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const { scan } = useGetScanAndRepository({ scanId })
  const isSca = scan ? isScaTool(scan.detector) : false
  const quickViewCounts = useFindingQuickViewCounts({ scanId })
  const previousStateRef = useRef<ScanLifecycleState['type'] | undefined>(undefined)

  useEffect(() => {
    const currentState = scanLifecycleState?.type
    const previousState = previousStateRef.current

    if (
      previousState === 'first-analysis-batch-in-progress' &&
      (currentState === 'findings-with-analysis-complete' ||
        currentState === 'findings-with-analysis-complete-and-in-progress')
    ) {
      const analyzedCount = isSca ? quickViewCounts['sca-analyzed'] : quickViewCounts.analyzed
      if (analyzedCount === 0) {
        setSelectedQuickView(isSca ? 'sca-unanalyzed' : 'unanalyzed')
      } else {
        setSelectedQuickView(isSca ? 'sca-analyzed' : 'analyzed')
      }
    }

    previousStateRef.current = currentState
  }, [scanLifecycleState?.type, setSelectedQuickView, isSca, quickViewCounts])
}
